'use client';

import { useEffect } from 'react';
import {
  isChunkLoadError,
  recoverFromChunkError,
  clearChunkRecoveryFlag,
} from '@/lib/ui/chunkRecovery';

/**
 * Reload once when a deploy orphans this page's chunks.
 *
 * This catches UNCAUGHT chunk failures. The caught ones — a try/catch that
 * shows `e.message` — are handled at the toast layer, which is where they
 * actually surfaced to users. Both use the same detection so they cannot
 * disagree about what a chunk failure is.
 */
export default function ChunkLoadRecovery() {
  useEffect(() => {
    // This mount proves the current build loaded, so a later genuine failure
    // is allowed its one reload.
    clearChunkRecoveryFlag();

    const handleError = (event: ErrorEvent) => {
      if (isChunkLoadError(event.error)) {
        recoverFromChunkError();
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (isChunkLoadError(event.reason)) {
        recoverFromChunkError();
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return null;
}
